import { useState, useEffect } from 'react';

import { Info } from 'components/info';
import { useAppSelector } from 'store';
import { capitalize } from 'utils/string';
import { Row, Col } from 'components/grid';
import { ExpensesType, ButtonColors } from 'types';
import { useCreateMutation } from 'store/api/payout';
import { Dialog, DialogType } from 'components/dialog';
import { Button, ButtonTypes } from 'components/button';
import { PaymentsForm } from 'components/payments-form';
import { selectAdvanceAccess } from 'store/slices/auth';
import { useCategoriesQuery } from 'store/api/accounting';
import { useAlert, isMobile, handleMutation } from 'hooks';
import { DropZoneContent } from 'components/drop-zone-content';
import { NewEntityButton } from 'components/new-entity-button';
import { formatPrice, handleFormSubmitWithFiles } from 'utils/helpers';
import { useDetailsQuery, useCashManagerQuery } from 'store/api/event';
import { Messages, AdvanceAccess, acceptPdfAndImageFileTypes } from 'config';
import {
  SelectField,
  CheckboxField,
  RadioFieldGroup,
  FileDropzoneField,
} from 'components/fields';

import {
  FormValues,
  ExpenseTypeCashStatus,
  CreateBonusAdvanceDialogProps,
} from './types';
import {
  getInitialValues,
  isOverRegisterCash,
  getValidationSchema,
  hideReceiptFieldTypes,
  shouldShowCashFromEvent,
  CASH_ONLY_ADVANCE_LABEL,
  sanitizePayoutFormValues,
  expenseTypeToPayoutMapping,
  getAvailableExpenseTypeValues,
} from './config';

const ClearCashFromEventWhenHidden = ({
  shouldClear,
  setFieldValue,
  isCashFromEvent,
}: {
  shouldClear: boolean;
  isCashFromEvent: boolean;
  setFieldValue: (
    field: string,
    value: boolean,
    shouldValidate?: boolean
  ) => void;
}) => {
  useEffect(() => {
    if (shouldClear && isCashFromEvent) {
      setFieldValue('isCashFromEvent', false, false);
    }
  }, [isCashFromEvent, setFieldValue, shouldClear]);

  return null;
};

// The show picked in the form, when the caller passes none (the Payroll
// dashboard's New Advance): its register cash is read for that show.
const TrackPickedEvent = ({
  eventId,
  onChange,
}: {
  eventId?: number | string;
  onChange: (eventId?: number) => void;
}) => {
  useEffect(() => {
    onChange(eventId ? Number(eventId) : undefined);
  }, [eventId, onChange]);

  return null;
};

export const CreateBonusAdvanceDialog = ({
  eventId,
  trigger,
  employeeId,
  isAdvances,
  withReceipt,
  customTitle,
  isWideDialog,
  customEvents,
  initialValues,
  isAmountLocked,
  onSuccessSubmit,
  typeGridOnPhone,
  customTypeValues,
  hideCashFromEvent,
  onSubmittingChange,
  isOnlyCashLocation,
  isDisableEvent = true,
  customEmployeeOptions,
  showSelectExpensesType,
}: CreateBonusAdvanceDialogProps) => {
  const mobile = isMobile();
  const [create, mutation] = useCreateMutation();
  const { handleError, showSuccessAlert } = useAlert();
  const { data, isFetching } = useDetailsQuery(String(eventId), {
    skip: !eventId || !!customEmployeeOptions,
  });
  const [pickedEventId, setPickedEventId] = useState<number>();
  // ORCESTRATOR Dev check 26 Oct: the register line must show whenever cash
  // is ticked, also when the show is picked in the form.
  const cashEventId = eventId || pickedEventId;
  const { data: cashData, isFetching: isCashFetching } = useCashManagerQuery(
    String(cashEventId),
    { skip: !cashEventId }
  );

  const { data: categories, isFetching: isCategoryFetching } =
    useCategoriesQuery();

  const categoryOptions = categories?.map((value) => ({
    label: value?.name,
    value: value?.id?.toString(),
  }));

  // Owner 26 Oct 2026: a Manager / Admin Rep gives an advance only as cash
  // handed out at the show, up to the show's register cash.
  const advanceAccess = useAppSelector(selectAdvanceAccess);
  const isCashOnlyAdvance =
    !!isAdvances && advanceAccess === AdvanceAccess.Cash;
  const registerCash = cashData?.balance;

  const title = isAdvances ? 'advance' : 'bonus';
  handleMutation(mutation);
  useEffect(() => {
    onSubmittingChange?.(mutation.isLoading);
    return () => onSubmittingChange?.(false);
  }, [mutation.isLoading, onSubmittingChange]);

  const typeValues = getAvailableExpenseTypeValues({
    customTypeValues,
    isOnlyCashLocation,
  });

  const employeeOptions =
    customEmployeeOptions ||
    [data?.leader, ...(data?.teamMembers || [])]
      .filter(Boolean)
      .map((value) => ({
        label: String(value?.name),
        value: String(value?.employee?.id),
      }));

  const onSubmit = async (formValues: FormValues, close: () => void) => {
    const values = sanitizePayoutFormValues(formValues);
    const defaultType = isAdvances ? ExpensesType.Advance : ExpensesType.Bonus;

    const cashStatus = values.isCashFromEvent
      ? ExpenseTypeCashStatus.Prepaid
      : ExpenseTypeCashStatus.Regular;

    const type =
      expenseTypeToPayoutMapping[values.type || defaultType]?.[cashStatus];

    const response = await create({
      ...values,
      type,
      amount: +values.amount,
      employeeId: +values.employeeId,
      categoryId: values?.categoryId ? +values.categoryId : undefined,
    }).unwrap();

    if (response.id) {
      onSuccessSubmit?.({ ...values, id: response.id });
      close();
      showSuccessAlert(`${type} ${Messages.ADDED_SUCCESSFULLY}`);
    }
  };

  return (
    <Dialog
      title={customTitle || `Create ${title}`}
      width={isWideDialog ? (mobile ? '90vw' : '600px') : undefined}
      trigger={
        trigger || <NewEntityButton text={customTitle || `Add ${title}`} />
      }
    >
      {(close, _, ButtonWrap) => (
        <PaymentsForm<FormValues>
          isDialog
          isTopChildren
          hideBackButton
          customEvents={customEvents}
          isDisableEvent={isDisableEvent}
          isDisabledAmount={isCashFetching || isAmountLocked}
          validationSchema={getValidationSchema({
            withReceipt,
            showSelectExpensesType,
            cashFlowBalance: cashData?.balance,
          })}
          initialValues={{
            ...getInitialValues({
              eventId,
              employeeId,
              initialValues,
              isOnlyCashLocation,
            }),
            ...(isCashOnlyAdvance ? { isCashFromEvent: true } : {}),
          }}
          // ORCESTRATOR 26 Oct (UI rule): the register line sits right under
          // the Event field it reads.
          afterEvent={({ values }) =>
            isAdvances &&
            shouldShowCashFromEvent({ type: values.type, hideCashFromEvent }) &&
            values.isCashFromEvent &&
            typeof registerCash === 'number' ? (
              <Row>
                <Col>
                  <Info
                    noEllipsis
                    title="Cash in the register"
                    value={formatPrice(registerCash)}
                  />
                </Col>
              </Row>
            ) : null
          }
          onSubmit={
            withReceipt
              ? handleFormSubmitWithFiles({
                  data: {},
                  handleError,
                  onSubmit: (values) => onSubmit(values, close),
                  files: [
                    {
                      formFieldName: 'receipt',
                      dataFieldName: 'receiptUrl',
                      requestFieldName: 'receiptUrl',
                    },
                  ],
                })
              : (values) => onSubmit(values, close)
          }
          footerButtons={({ dirty, values, isSubmitting }) => (
            <ButtonWrap type={DialogType.Plain}>
              <Button
                text="Cancel"
                onClick={close}
                fullWidth={mobile}
                colorType={ButtonColors.LightBlue}
              />
              <Button
                text="Add"
                fullWidth={mobile}
                type={ButtonTypes.Submit}
                disabled={
                  !dirty ||
                  isSubmitting ||
                  isOverRegisterCash({
                    isAdvance: isAdvances,
                    amount: values.amount,
                    balance: registerCash,
                    isCashFromEvent: values.isCashFromEvent,
                  })
                }
              />
            </ButtonWrap>
          )}
        >
          {({ values, setFieldValue }) => {
            const isShowReceipt =
              withReceipt &&
              values?.type &&
              !hideReceiptFieldTypes.includes(values?.type as ExpensesType);
            const isShowCashFromEvent = shouldShowCashFromEvent({
              type: values.type,
              hideCashFromEvent,
            });

            return (
              <>
                {!eventId && (
                  <TrackPickedEvent
                    eventId={values.eventId}
                    onChange={setPickedEventId}
                  />
                )}

                <ClearCashFromEventWhenHidden
                  setFieldValue={setFieldValue}
                  shouldClear={!isShowCashFromEvent}
                  isCashFromEvent={values.isCashFromEvent}
                />

                {showSelectExpensesType && (
                  <Row>
                    <Col>
                      <RadioFieldGroup
                        name="type"
                        transparent
                        padding="0"
                        label="Select Type"
                        items={typeValues.map((value) => ({
                          value,
                          label: capitalize(value),
                        }))}
                        grid={
                          typeGridOnPhone && mobile
                            ? {
                                gap: '4px 16px',
                                template: 'auto / repeat(2, max-content)',
                              }
                            : undefined
                        }
                      />
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col>
                    <SelectField
                      onlyValue
                      label="Employee"
                      name="employeeId"
                      options={employeeOptions}
                      isDisabled={
                        isFetching ||
                        // A single fixed employee (Paycheck / Payout "Add
                        // row", QuickBooks): locked. A list (the Payroll
                        // dashboard's New Advance / Issue Bonus / Add Payout
                        // Item) is picked from (Dev check 26 Oct).
                        customEmployeeOptions?.length === 1
                      }
                    />
                  </Col>
                </Row>

                {isShowCashFromEvent && (
                  <Row>
                    <Col>
                      <CheckboxField
                        name="isCashFromEvent"
                        disabled={isOnlyCashLocation || isCashOnlyAdvance}
                        label={
                          isCashOnlyAdvance
                            ? CASH_ONLY_ADVANCE_LABEL
                            : 'Cash From Location'
                        }
                      />
                    </Col>
                  </Row>
                )}

                {isShowReceipt && (
                  <>
                    <Row>
                      <Col>
                        <FileDropzoneField
                          errorMessage
                          name="receipt"
                          label="Upload receipt"
                          accept={acceptPdfAndImageFileTypes}
                        >
                          <DropZoneContent file={values.receipt} />
                        </FileDropzoneField>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <SelectField
                          onlyValue
                          name="categoryId"
                          label="Select category"
                          options={categoryOptions}
                          placeholder="Select category"
                          isDisabled={isCategoryFetching}
                        />
                      </Col>
                    </Row>
                  </>
                )}
              </>
            );
          }}
        </PaymentsForm>
      )}
    </Dialog>
  );
};
